import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, Scale, Shield, AlertTriangle, Users, 
  CreditCard, Ban, RefreshCw, Globe, Mail
} from "lucide-react";

const TermsOfService = () => {
  const lastUpdated = "May 5, 2026";

  const sections = [
    {
      id: "acceptance",
      icon: FileText,
      title: "1. Acceptance of Terms",
      color: "text-blue-400",
      content: `By creating an account or using Pivot Performance ("the App"), you agree to these Terms of Service. If you do not agree, do not use the App. These terms constitute a legally binding agreement between you and Pivot Point Athletics, LLC, a California limited liability company ("Company," "we," "us") that owns and operates Pivot Performance.`
    },
    {
      id: "health-warning",
      icon: AlertTriangle,
      title: "2. Health & Safety Disclaimer",
      color: "text-red-400",
      content: null,
      subsections: [
        {
          title: "Medical Advisory",
          items: [
            "Pivot Performance is NOT a medical provider or licensed fitness professional",
            "Consult a physician before beginning the Break the Cycle program",
            "Basketball conditioning involves risks including muscle strains, joint injuries, cardiac events, or death",
            "You are solely responsible for assessing your physical capabilities"
          ]
        },
        {
          title: "Assumption of Risk",
          items: [
            "You voluntarily assume all risks associated with using the App",
            "If you feel pain, dizziness, or shortness of breath, STOP IMMEDIATELY",
            "AI coaching recommendations are general guidance, not medical advice",
            "Video analysis feedback is algorithmic and should not replace professional coaching"
          ]
        }
      ]
    },
    {
      id: "liability",
      icon: Scale,
      title: "3. Limitation of Liability",
      color: "text-amber-400",
      content: `To the maximum extent permitted by law, Pivot Point Athletics, LLC and its owners, employees, members, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the App. Our total liability shall not exceed the amount you paid for subscription services in the preceding 12 months. You expressly waive any protections of California Civil Code Section 1542.`
    },
    {
      id: "accounts",
      icon: Users,
      title: "4. User Accounts",
      color: "text-green-400",
      content: null,
      subsections: [
        {
          title: "Account Responsibilities",
          items: [
            "You must provide accurate information during registration",
            "You are responsible for maintaining the security of your account",
            "You must be at least 16 years old to create an account",
            "One account per person — sharing accounts is prohibited",
            "We may suspend or terminate accounts that violate these terms"
          ]
        }
      ]
    },
    {
      id: "subscriptions",
      icon: CreditCard,
      title: "5. Subscriptions & Payments",
      color: "text-purple-400",
      content: null,
      subsections: [
        {
          title: "Billing",
          items: [
            "Subscription fees are billed monthly via Stripe",
            "Prices may change with 30 days notice to active subscribers",
            "Taxes may apply based on your jurisdiction",
            "Failed payments may result in service interruption"
          ]
        },
        {
          title: "Refunds & Cancellation",
          items: [
            "You may cancel your subscription at any time",
            "Access continues through the end of the billing period",
            "No refunds for partial months of service",
            "Beta users receive complimentary access as specified in beta terms"
          ]
        }
      ]
    },
    {
      id: "content",
      icon: Shield,
      title: "6. Intellectual Property",
      color: "text-cyan-400",
      content: `All content within the App — including workout programs, drill descriptions, manga illustrations, AI coaching algorithms, and application design — is the exclusive property of Pivot Point Athletics, LLC. You may not reproduce, distribute, or create derivative works without written permission. User-generated content (progress photos, workout logs) remains your property.`
    },
    {
      id: "prohibited",
      icon: Ban,
      title: "7. Prohibited Conduct",
      color: "text-red-400",
      content: null,
      subsections: [
        {
          title: "You agree NOT to:",
          items: [
            "Use the App for any unlawful purpose",
            "Attempt to reverse-engineer or hack the App",
            "Share, resell, or redistribute subscription access",
            "Upload harmful, offensive, or illegal content",
            "Use automated systems (bots) to interact with the App",
            "Impersonate other users or misrepresent your identity"
          ]
        }
      ]
    },
    {
      id: "modifications",
      icon: RefreshCw,
      title: "8. Changes to Terms",
      color: "text-orange-400",
      content: `We reserve the right to modify these Terms at any time. Material changes will be communicated via email or in-app notification at least 14 days before taking effect. Continued use of the App after changes constitutes acceptance of the modified terms.`
    },
    {
      id: "governing-law",
      icon: Globe,
      title: "9. Governing Law & Disputes",
      color: "text-indigo-400",
      content: `These Terms are governed by the laws of the State of California, USA. Any disputes shall be resolved through binding arbitration in accordance with the American Arbitration Association rules. Class action lawsuits are waived. Small claims court actions are permitted.`
    },
    {
      id: "contact",
      icon: Mail,
      title: "10. Contact",
      color: "text-emerald-400",
      content: `For questions about these Terms of Service or any account/billing issues, contact us at:

Pivot Point Athletics, LLC
1624 E. Stafford St
Santa Ana, CA 92701
Email: pivotpointathletics@gmail.com`
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto space-y-6"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-orange-500/20 border border-orange-500/30 flex items-center justify-center rounded-sm">
              <Scale className="w-6 h-6 text-orange-500" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-white uppercase tracking-tight">
            Terms of Service
          </h1>
          <p className="text-zinc-400 mt-2">Pivot Performance — Basketball Conditioning<br/><span className="text-xs text-zinc-500">Owned and operated by Pivot Point Athletics, LLC · California, USA</span></p>
          <Badge className="mt-3 bg-zinc-800 text-zinc-300 border-zinc-700">
            Last Updated: {lastUpdated}
          </Badge>
        </div>

        {/* Sections */}
        {sections.map((section, idx) => (
          <motion.div
            key={section.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <Card className="bg-zinc-900/80 border-zinc-800" data-testid={`tos-section-${section.id}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3">
                  <section.icon className={`w-5 h-5 ${section.color}`} />
                  <span className="text-white font-bold text-base">{section.title}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {section.content && (
                  <p className="text-zinc-400 text-sm leading-relaxed">{section.content}</p>
                )}
                {section.subsections && section.subsections.map((sub, subIdx) => (
                  <div key={subIdx} className={subIdx > 0 ? "mt-4" : ""}>
                    <h4 className="text-zinc-300 font-semibold text-sm mb-2">{sub.title}</h4>
                    <ul className="space-y-1.5">
                      {sub.items.map((item, itemIdx) => (
                        <li key={itemIdx} className="flex items-start gap-2 text-zinc-400 text-sm">
                          <span className="text-orange-500 mt-1 text-xs">&#9654;</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {/* Footer */}
        <div className="text-center py-6 border-t border-zinc-800">
          <p className="text-zinc-500 text-xs">
            By using Pivot Performance, you acknowledge that you have read, understood, and agree to these Terms of Service.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default TermsOfService;
